/**
 * Sentry error tracking — web.
 *
 * Initialization is gated behind the REACT_APP_SENTRY_DSN env var: if no DSN is
 * configured, initSentry() is a complete no-op. This means the app ships safely
 * without a DSN and tracking only turns on once the env var is set in the
 * deployment (Vercel → Project → Settings → Environment Variables).
 *
 * PRIVACY: GOACTO stores personal learning reflections and user-supplied AI API
 * keys. We aggressively scrub both from every event before it leaves the device.
 */
import * as Sentry from '@sentry/react';

const DSN = process.env.REACT_APP_SENTRY_DSN;

// Redact anything that looks like an API key or long free-text reflection.
const scrub = (value: unknown): unknown => {
  if (typeof value !== 'string') return value;
  // Mask provider API keys (Anthropic sk-ant-, OpenAI sk-, Grok xai-).
  if (/\b(sk-ant-|sk-|xai-)[A-Za-z0-9_-]{8,}/.test(value)) return '[redacted-api-key]';
  return value;
};

export function initSentry(): void {
  if (!DSN) return; // No DSN configured — tracking disabled.

  Sentry.init({
    dsn: DSN,
    environment: process.env.NODE_ENV,
    release: process.env.REACT_APP_VERSION,
    // Capture 10% of transactions for performance; errors are always captured.
    tracesSampleRate: 0.1,
    // Never send the user's IP or default PII.
    sendDefaultPii: false,
    beforeSend(event) {
      // Strip request data that could contain reflection text.
      if (event.request) {
        delete event.request.data;
        delete event.request.cookies;
      }
      // Scrub breadcrumb messages.
      if (event.breadcrumbs) {
        event.breadcrumbs = event.breadcrumbs.map(b => ({ ...b, message: scrub(b.message) as string | undefined }));
      }
      return event;
    },
  });
}

export { Sentry };
